import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '../../theme/theme-provider';

const GLYPHS = '!@#$%<>[]01ABCDEFabcdef?/\\|=+~^&*';

function AmbientGlyphs({ color = 'rgba(255,34,0,0.05)', speed = 28 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let raf;
    let resizeRaf;
    let w = 0;
    let h = 0;
    let lastFrameTime = 0;
    const frameInterval = 1000 / 30; // Cap at 30fps for performance

    const resize = () => {
      const p = canvas.parentElement;
      if (!p) return;
      const nextW = p.clientWidth;
      const nextH = p.clientHeight;
      if (!nextW || !nextH || (nextW === w && nextH === h)) return;
      w = nextW;
      h = nextH;
      canvas.width = nextW;
      canvas.height = nextH;
    };

    const scheduleResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(resize);
    };

    const ro = new ResizeObserver(scheduleResize);
    ro.observe(canvas.parentElement);
    scheduleResize();

    const draw = (timestamp) => {
      raf = requestAnimationFrame(draw);
      
      // Throttle to 30fps
      if (timestamp - lastFrameTime < frameInterval) return;
      lastFrameTime = timestamp;
      
      if (!w || !h) return;

      const t = performance.now() / 1000;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, w, h);
      ctx.font = '13px "Courier New", monospace';
      ctx.fillStyle = color;

      const cols = Math.ceil(w / 16);
      for (let c = 0; c < cols; c++) {
        const idx = Math.floor((t * speed * 0.12 + c * 17.3) % GLYPHS.length);
        const y = ((t * speed + c * 37.7) % h);
        ctx.fillText(GLYPHS[Math.floor(idx)], c * 16, y);
      }
    };

    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeRaf);
      ro.disconnect();
    };
  }, [color, speed]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        willChange: 'transform'
      }}
    />
  );
}

const ThemeAmbientOverlay = ({ speed = 28, motionBoost = false }) => {
  const { themeMode } = useTheme();
  const glyphColor = themeMode === 'dark' ? 'rgba(255,52,20,0.18)' : 'rgba(0,0,0,0.16)';
  const boostGlyphColor = themeMode === 'dark' ? 'rgba(255,70,30,0.09)' : 'rgba(0,0,0,0.08)';

  return (
    <>
      <AmbientGlyphs color={glyphColor} speed={speed} />
      {motionBoost && <AmbientGlyphs color={boostGlyphColor} speed={speed + 10} />}
      {themeMode === 'dark' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            willChange: 'transform',
            backgroundImage: [
              'repeating-linear-gradient(8deg, rgba(255,255,255,0.038) 0 1px, transparent 1px 9px)',
              'repeating-linear-gradient(-14deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 14px)',
              'linear-gradient(180deg, rgba(255,255,255,0.025), rgba(0,0,0,0.07))'
            ].join(',')
          }}
        />
      )}
      {themeMode === 'light' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            willChange: 'transform',
            backgroundImage: [
              'repeating-linear-gradient(8deg, rgba(0,0,0,0.038) 0 1px, transparent 1px 9px)',
              'repeating-linear-gradient(-14deg, rgba(0,0,0,0.025) 0 1px, transparent 1px 14px)',
              'linear-gradient(180deg, rgba(0,0,0,0.025), rgba(255,255,255,0.07))'
            ].join(',')
          }}
        />
      )}
    </>
  );
};

export default ThemeAmbientOverlay;
